import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useLenis from "@/lib/useLenis";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Cursor from "@/components/site/Cursor";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

// Full client roster grouped by discipline. Copy provided by hankdavid.com.
const CATEGORIES = [
  {
    label: "Residential",
    clients: [
      "600 NE Ivy",
      "Copper Crest Townhomes",
      "Powder Mountain Custom Homes",
      "Freres Residence, Salishan",
      "Thurman Lofts",
      "Gerding Edlen / Capitol Hill Seattle",
      "Prometheus / The Benton",
      "Eastside Lofts",
      "Emerson House",
      "Ivy Residence",
      "Jones Residence",
      "Mississippi Avenue Lofts",
      "Tabor House",
      "Thurman Residence",
    ],
  },
  {
    label: "Workplace",
    clients: [
      "ArcTeryx",
      "Friends of the Children, NCOE",
      "FBFK Law",
      "Hoﬀman Construction HQ",
      "NIKE World Headquarters",
      "Skylab HQ",
      "Studio Mega",
      "Acumed HQ",
      "City Club of Portland",
      "Knight Cancer Institute",
      "Oregon State Capitol",
      "Portland State University",
      "SAIF Corporation",
      "SRG HQ",
    ],
  },
  {
    label: "Hospitality",
    clients: [
      "Alyeska Resort",
      "Brasada Ranch",
      "Compañeros",
      "Deer Valley Resort",
      "Empire Control Room, Austin",
      "NORD",
      "Powder Mountain Resort",
      "Royal Caribbean Group",
      "Schweitzer Mountain, Humbird Hotel",
      "Terra Vi Eco Resort",
      "Tungsten Lodge",
      "W Hotel San Francisco",
      "Knot Springs, Portland",
      "Knot Springs, Seattle",
    ],
  },
  {
    label: "Retail & Brand",
    clients: [
      "Hoﬀman Construction",
      "Maserati North America",
      "Scout Motors",
      "Studio MEGA",
      "Travis Mathew",
      "American University of Paris",
      "Mississippi Avenue Lofts",
      "Prometheus Group / The Benton",
      "Shop Dog",
      "SRG Partnership",
      "Skylab",
    ],
  },
  {
    label: "Education",
    clients: [
      "American University of Paris, France",
      "Chemeketa Community College",
      "Claremont Colleges + Keck Institute",
      "Lane Community College",
      "Marylhurst University",
      "Oregon Health Sciences University",
      "Oregon State University",
      "Pacific University",
      "Portland State University",
      "University of Colorado, Boulder",
      "University of Oregon",
      "Washington School for the Deaf",
    ],
  },
  {
    label: "Athletics",
    clients: [
      "Hillsboro Hops Stadium / Tonkin Field",
      "University of Oregon \u2014 Jane Sanders Stadium",
      "University of Oregon \u2014 Marcus Mariota Sports Performance Center",
    ],
  },
  {
    label: "Civic / Non-Profit",
    clients: [
      "City of Bishop",
      "City of Hillsboro",
      "City of Portland",
      "Friends of the Children",
      "Inyo County",
      "Multnomah County Courthouse",
      "Oregon State Capitol",
      "Hope Village",
      "Sisters of the Road",
    ],
  },
  {
    label: "Healthcare",
    clients: [
      "BioLounge",
      "Oregon State Hospital",
      "Pacific University Health Professions",
      "Providence Center for Medically Fragile Children",
      "Shriners Hospitals for Children, Honolulu",
      "Shriners Hospitals for Children, Lexington",
      "Shriners Hospitals for Children, Portland",
      "The Research & Education Group",
    ],
  },
];

export default function ClientList() {
  useLenis();

  useEffect(() => {
    document.title = "Client List — HANKDAVID";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App grain min-h-screen bg-[color:var(--hd-bg)] text-white">
      <Cursor />
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        {/* Header */}
        <header className="px-6 md:px-10 max-w-[1600px] mx-auto">
          <div className="flex items-center gap-4 mb-14 md:mb-20">
            <span className="mono-label">04 &mdash; Client List</span>
            <div className="hairline flex-1 max-w-32" />
            <Link
              to="/#studio"
              data-testid="client-list-back"
              data-cursor="←"
              className="mono-label text-white/60 hover:text-white transition-colors"
            >
              &larr; Back to studio
            </Link>
          </div>

          <h1 className="font-display !normal-case text-[8vw] md:text-[5.5vw] lg:text-[4.5vw] leading-[0.94] tracking-[-0.03em] whitespace-nowrap">
            <Line delay={0.05}>Who we&rsquo;ve worked with</Line>
          </h1>
        </header>

        <div className="hairline mt-8 md:mt-10" />

        {/* Category grid */}
        <section className="px-6 md:px-10 max-w-[1600px] mx-auto mt-8 md:mt-10">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-10%" }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.06 } },
            }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14 md:gap-y-20"
          >
            {CATEGORIES.map((cat) => (
              <motion.div
                key={cat.label}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: EASE },
                  },
                }}
                className="flex flex-col"
              >
                <div className="hairline mb-5" />
                <h2 className="font-display !normal-case text-base md:text-lg tracking-tight text-white mb-6 md:mb-8">
                  {cat.label}
                </h2>
                <ul className="space-y-2.5 md:space-y-3">
                  {cat.clients.map((name) => (
                    <li
                      key={name}
                      className="text-white/70 text-sm md:text-[15px] leading-snug"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Footer note */}
        <div className="px-6 md:px-10 max-w-[1600px] mx-auto mt-24 md:mt-32">
          <div className="hairline mb-6" />
          <p className="text-white/40 text-xs md:text-sm max-w-2xl leading-relaxed">
            Client list includes clients worked with over the last 20 years while at
            several design firms.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
